import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ADJUSTMENTS } from '../../adjustments';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../../core/color';
import {
  GRADIENT_TYPES,
  GRADIENT_TYPE_LABELS,
  gradientToCss,
} from '../../core/gradient';
import { findLayerById } from '../../core/layerTree';
import { allPatterns, getPattern } from '../../core/patterns';
import { TEXT_ALIGN_OPTIONS, TEXT_FONT_OPTIONS } from '../../core/textFonts';
import type {
  AdjustmentState,
  FillContent,
  FillLayer,
  TextAlign,
  TextLayer,
  RGBA,
} from '../../core/types';
import {
  beginMetaTransaction,
  commitMetaTransaction,
  setAdjustmentLayerState,
  setFillLayerContent,
} from '../../state/actions';
import {
  editTextLayer,
  setTextLayerProps,
  updateTextTransient,
} from '../../state/textActions';
import { getTextEditSession, subscribeTextEdit } from '../../state/textEdit';
import { getActiveDocument, useAppStore } from '../../state/store';
import { AdjustmentEditor } from '../components/AdjustmentEditor';
import { Checkbox, NumberInput, Select, SliderRow } from '../components/controls';
import { GradientEditor } from '../dialogs/GradientEditor';
import { Panel } from './Panel';

const FILL_KIND_LABELS: Record<FillContent['kind'], string> = {
  solid: 'Solid Color',
  gradient: 'Gradient Fill',
  pattern: 'Pattern Fill',
};

/**
 * Edits the active adjustment or fill layer. Changes are written live and
 * committed as one history step when the pointer leaves the panel, so dragging
 * a slider does not flood the history stack.
 */
export function PropertiesPanel() {
  const doc = useAppStore(getActiveDocument);
  const layer = doc?.activeLayerId ? findLayerById(doc.layers, doc.activeLayerId) : null;
  const isAdjustment = layer?.kind === 'adjustment';
  const isFill = layer?.kind === 'fill';
  const isText = layer?.kind === 'text';

  if (!isAdjustment && !isFill && !isText) {
    return (
      <Panel id="properties" title="Properties">
        <div className="panel__body">
          <p className="dialog__note">
            Select a text, adjustment, or fill layer to edit its settings.
          </p>
        </div>
      </Panel>
    );
  }

  return (
    <Panel id="properties" title="Properties">
      <div className="panel__body">
        {isAdjustment && <AdjustmentProperties layerId={layer.id} state={layer.adjustment} />}
        {isFill && <FillProperties layer={layer} />}
        {isText && <TextProperties layer={layer} />}
      </div>
    </Panel>
  );
}

function TextProperties({ layer }: { layer: TextLayer }) {
  const setForeground = useAppStore((state) => state.setForeground);
  const session = useSyncExternalStore(subscribeTextEdit, getTextEditSession, getTextEditSession);
  const editingThis = session?.layerId === layer.id;

  const apply = (patch: {
    content?: string;
    fontFamily?: string;
    fontSize?: number;
    align?: TextAlign;
    fontWeight?: 'normal' | 'bold';
    fontStyle?: 'normal' | 'italic';
    color?: RGBA;
  }) => {
    if (patch.color) setForeground(patch.color);
    if (editingThis) updateTextTransient(patch);
    else setTextLayerProps(layer.id, patch);
  };

  const fonts = TEXT_FONT_OPTIONS.some((f) => f.value === layer.fontFamily)
    ? TEXT_FONT_OPTIONS
    : [{ value: layer.fontFamily, label: layer.fontFamily }, ...TEXT_FONT_OPTIONS];

  return (
    <>
      <p className="dialog__note">Character</p>
      <label className="field field--stack">
        <span className="field__label">Content</span>
        <textarea
          className="text-input text-input--area"
          rows={3}
          value={layer.content}
          onChange={(event) => apply({ content: event.target.value })}
        />
      </label>
      <div className="field">
        <span className="field__label">Font</span>
        <Select
          value={layer.fontFamily}
          options={fonts}
          onChange={(fontFamily) => apply({ fontFamily })}
        />
      </div>
      <div className="field">
        <span className="field__label">Size</span>
        <NumberInput
          value={Math.round(layer.fontSize)}
          min={4}
          max={1000}
          onChange={(fontSize) => apply({ fontSize })}
        />
        <span className="field__suffix">px</span>
      </div>
      <div className="field">
        <span className="field__label">Color</span>
        <label
          className="toolbar__swatch"
          style={{
            position: 'relative',
            width: 44,
            height: 22,
            background: rgbaToCss(layer.color),
          }}
        >
          <input
            type="color"
            style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            value={rgbaToHex(layer.color)}
            onChange={(event) => apply({ color: hexToRgba(event.target.value) })}
          />
        </label>
      </div>
      <div className="field">
        <span className="field__label">Align</span>
        <Select
          value={layer.align}
          options={TEXT_ALIGN_OPTIONS}
          onChange={(align) => apply({ align })}
        />
      </div>
      <Checkbox
        label="Bold"
        checked={layer.fontWeight === 'bold'}
        onChange={(next) => apply({ fontWeight: next ? 'bold' : 'normal' })}
      />
      <Checkbox
        label="Italic"
        checked={layer.fontStyle === 'italic'}
        onChange={(next) => apply({ fontStyle: next ? 'italic' : 'normal' })}
      />
      <div className="field" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="button button--small"
          onClick={() => editTextLayer(layer.id)}
          disabled={editingThis}
        >
          {editingThis ? 'Editing on canvas…' : 'Edit Text'}
        </button>
      </div>
    </>
  );
}

function useLiveCommit(layerId: string) {
  const pending = useRef(false);
  useEffect(() => {
    pending.current = false;
  }, [layerId]);

  const begin = () => {
    if (pending.current) return;
    beginMetaTransaction();
    pending.current = true;
  };
  const end = () => {
    if (!pending.current) return;
    commitMetaTransaction('Layer Properties');
    pending.current = false;
  };
  return { begin, end };
}

function AdjustmentProperties({
  layerId,
  state,
}: {
  layerId: string;
  state: AdjustmentState;
}) {
  const { begin, end } = useLiveCommit(layerId);
  const [editingGradient, setEditingGradient] = useState(false);
  const gradientPresets = useAppStore((store) => store.gradient.presets);
  const definition = ADJUSTMENTS[state.id];

  return (
    <>
      <p className="dialog__note">{definition.label}</p>
      <div onPointerDown={begin} onPointerUp={end} onPointerLeave={end}>
        <AdjustmentEditor
          state={state}
          onChange={(next) => setAdjustmentLayerState(layerId, next, false)}
          onEditGradient={() => {
            begin();
            setEditingGradient(true);
          }}
        />
      </div>
      {editingGradient && state.gradient && (
        <GradientEditor
          gradient={state.gradient}
          presets={gradientPresets}
          onChange={(gradient) => setAdjustmentLayerState(layerId, { ...state, gradient }, false)}
          onClose={() => {
            setEditingGradient(false);
            end();
          }}
        />
      )}
    </>
  );
}

function FillProperties({ layer }: { layer: FillLayer }) {
  const { begin, end } = useLiveCommit(layer.id);
  const [editingGradient, setEditingGradient] = useState(false);
  const gradientPresets = useAppStore((store) => store.gradient.presets);
  const patterns = allPatterns();
  const fill = layer.fill;

  const set = (next: FillContent) => setFillLayerContent(layer.id, next, false);

  return (
    <>
      <p className="dialog__note">{FILL_KIND_LABELS[fill.kind]}</p>
      <div onPointerDown={begin} onPointerUp={end} onPointerLeave={end}>
        {fill.kind === 'solid' && (
          <div className="field">
            <span className="field__label">Color</span>
            <label
              className="toolbar__swatch"
              style={{
                position: 'relative',
                width: 44,
                height: 22,
                background: rgbaToCss(fill.color),
              }}
            >
              <input
                type="color"
                style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                value={rgbaToHex(fill.color)}
                onChange={(event) => set({ kind: 'solid', color: hexToRgba(event.target.value) })}
              />
            </label>
          </div>
        )}

        {fill.kind === 'gradient' && (
          <>
            <div className="field">
              <span className="field__label">Gradient</span>
              <button
                type="button"
                className="optionsbar__gradient"
                title="Edit the gradient"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${gradientToCss(fill.gradient)})`,
                }}
                onClick={() => {
                  begin();
                  setEditingGradient(true);
                }}
              />
            </div>
            <div className="field">
              <span className="field__label">Style</span>
              <Select
                value={fill.type}
                options={GRADIENT_TYPES.map((type) => ({
                  value: type,
                  label: GRADIENT_TYPE_LABELS[type],
                }))}
                onChange={(type) => set({ ...fill, type })}
              />
            </div>
            <SliderRow
              label="Angle"
              value={fill.angle}
              min={-180}
              max={180}
              onChange={(angle) => set({ ...fill, angle })}
            />
            <SliderRow
              label="Scale"
              value={fill.scale}
              min={10}
              max={150}
              onChange={(scale) => set({ ...fill, scale })}
            />
            <label className="field">
              <input
                type="checkbox"
                checked={fill.reverse}
                onChange={(event) => set({ ...fill, reverse: event.target.checked })}
              />
              <span className="field__label">Reverse</span>
            </label>
          </>
        )}

        {fill.kind === 'pattern' && (
          <>
            <div className="field">
              <span className="field__label">Pattern</span>
              <Select
                value={fill.patternId}
                options={patterns.map((pattern) => ({
                  value: pattern.id,
                  label: `${pattern.name} (${pattern.width}×${pattern.height})`,
                }))}
                onChange={(patternId) => set({ ...fill, patternId })}
              />
            </div>
            {getPattern(fill.patternId) && (
              <p className="dialog__note">
                {getPattern(fill.patternId)!.width}×{getPattern(fill.patternId)!.height} px tile
              </p>
            )}
            <div className="field">
              <span className="field__label">Scale</span>
              <NumberInput
                value={fill.scale}
                min={1}
                max={1000}
                onChange={(scale) => set({ ...fill, scale })}
              />
              <span className="field__suffix">%</span>
            </div>
          </>
        )}
      </div>

      {editingGradient && fill.kind === 'gradient' && (
        <GradientEditor
          gradient={fill.gradient}
          presets={gradientPresets}
          onChange={(gradient) => set({ ...fill, gradient })}
          onClose={() => {
            setEditingGradient(false);
            end();
          }}
        />
      )}
    </>
  );
}
